import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Recipient type options
const RECIPIENT_TYPES = [
  { value: 'waitlist', label: '📋 Waitlist Users', description: 'Users waiting for approval' },
  { value: 'all_users', label: '👥 All Active Users', description: 'All approved/active users' },
  { value: 'custom', label: '✏️ Custom List', description: 'Enter email addresses manually' }
];

const EmailCampaignManager = ({ adminToken }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('campaigns'); // campaigns, create, templates, logs
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [logs, setLogs] = useState([]);
  const [viewHtmlMode, setViewHtmlMode] = useState(false); // toggle rendered vs raw HTML in view modal
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Template editing state
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', subject: '', body_html: '', body_text: '' });
  const [templatePreview, setTemplatePreview] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Create form state
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body_html: '',
    body_text: '',
    recipient_type: 'waitlist',
    custom_emails: '',
    template_type: 'custom',
    scheduled_at: ''
  });
  
  const [recipientCount, setRecipientCount] = useState(0);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-campaigns/campaigns`, { 
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setCampaigns(response.data.campaigns || []);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-campaigns/templates`, { 
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-campaigns/logs`, { 
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setLogs(response.data.logs || []);
    } catch (err) {
      console.error('Error fetching logs:', err);
    }
  };

  const fetchRecipientCount = async (type) => {
    if (type === 'custom') {
      const emails = formData.custom_emails.split(/[\n,]/).filter(e => e.trim());
      setRecipientCount(emails.length);
      return;
    }
    try {
      const response = await axios.get(`${API}/admin/email-campaigns/recipients/count?recipient_type=${type}`, { 
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setRecipientCount(response.data.count || 0);
    } catch (err) {
      console.error('Error fetching recipient count:', err);
    }
  };

  useEffect(() => {
    if (!adminToken) return;
    
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCampaigns(), fetchTemplates(), fetchLogs()]);
      setLoading(false);
    };
    loadData();
  }, [adminToken]);

  useEffect(() => {
    if (!adminToken) return;
    fetchRecipientCount(formData.recipient_type);
  }, [adminToken, formData.recipient_type, formData.custom_emails]);

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setFormData(prev => ({
        ...prev,
        subject: template.subject,
        body_html: template.body_html,
        body_text: template.body_text,
        template_type: templateId,
        name: prev.name || template.name
      }));
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      subject: template.subject,
      body_html: template.body_html,
      body_text: template.body_text || ''
    });
    setTemplatePreview(false);
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    setSavingTemplate(true);
    try {
      await axios.put(
        `${API}/admin/email-campaigns/templates/${editingTemplate.id}`,
        templateForm,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      await fetchTemplates();
      setEditingTemplate(null);
      setMessage({ type: 'success', text: 'Template saved successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save template: ' + (error.response?.data?.detail || error.message) });
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleResetTemplate = async (templateId) => {
    if (!window.confirm('Reset this template to its original default content? Your customizations will be lost.')) return;
    try {
      await axios.post(
        `${API}/admin/email-campaigns/templates/${templateId}/reset`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      await fetchTemplates();
      if (editingTemplate?.id === templateId) setEditingTemplate(null);
      setMessage({ type: 'success', text: 'Template reset to default!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to reset: ' + (error.response?.data?.detail || error.message) });
    }
  };

  const handleCopyHtml = async (html) => {
    try {
      await navigator.clipboard.writeText(html);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = html;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleCreateCampaign = async (sendNow = false) => {
    if (!formData.name || !formData.subject || !formData.body_html) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    setSending(true);
    setMessage(null);

    try {
      const payload = {
        name: formData.name,
        subject: formData.subject,
        body_html: formData.body_html,
        body_text: formData.body_text,
        recipient_type: formData.recipient_type,
        custom_emails: formData.recipient_type === 'custom' 
          ? formData.custom_emails.split(/[\n,]/).map(e => e.trim()).filter(e => e)
          : null,
        template_type: formData.template_type,
        scheduled_at: formData.scheduled_at ? new Date(formData.scheduled_at).toISOString() : null
      };

      const response = await axios.post(`${API}/admin/email-campaigns/campaigns`, payload, { 
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      const campaign = response.data.campaign;

      if (sendNow) {
        await axios.post(`${API}/admin/email-campaigns/campaigns/${campaign.id}/send`, {}, { 
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        setMessage({ type: 'success', text: `✅ Campaign sent to ${recipientCount} recipients!` });
      } else {
        setMessage({ type: 'success', text: formData.scheduled_at ? '📅 Campaign scheduled!' : '💾 Campaign saved as draft!' });
      }

      fetchCampaigns();
      fetchLogs();
      setActiveTab('campaigns');
      resetForm();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to create campaign' });
    } finally {
      setSending(false);
    }
  };

  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to send this campaign now?')) return;
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/admin/email-campaigns/campaigns/${campaignId}/send`, {}, { 
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setMessage({ type: 'success', text: `✅ Sent to ${response.data.result.sent_count} recipients!` });
      fetchCampaigns();
      fetchLogs();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to send campaign' });
    } finally {
      setSending(false);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Delete this draft campaign?')) return;
    
    try {
      await axios.delete(`${API}/admin/email-campaigns/campaigns/${campaignId}`, { 
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setMessage({ type: 'success', text: 'Campaign deleted' });
      fetchCampaigns();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to delete' });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subject: '',
      body_html: '',
      body_text: '',
      recipient_type: 'waitlist',
      custom_emails: '',
      template_type: 'custom',
      scheduled_at: ''
    });
    setPreviewMode(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      scheduled: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      sending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
      sent: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">📧 Email Campaigns</h2>
          <p className="text-gray-600 dark:text-gray-400">Send bulk emails to your users</p>
        </div>
        <button
          onClick={() => { setActiveTab('create'); resetForm(); }}
          className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition-all"
        >
          + New Campaign
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
        {['campaigns', 'create', 'logs'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === tab
                ? 'bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-400 shadow'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {tab === 'campaigns' ? '📋 Campaigns' : tab === 'create' ? '✏️ Create' : '📊 Logs'}
          </button>
        ))}
      </div>

      {/* Campaigns List */}
      {activeTab === 'campaigns' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Campaign</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Recipients</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Sent/Failed</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {campaigns.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    No campaigns yet. Create your first one!
                  </td>
                </tr>
              ) : campaigns.map(campaign => (
                <tr key={campaign.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900 dark:text-white">{campaign.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">{campaign.subject}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                    {campaign.total_recipients} recipients
                    <div className="text-xs text-gray-400">{campaign.recipient_type}</div>
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(campaign.status)}</td>
                  <td className="px-6 py-4 text-sm">
                    <span className="text-green-600">{campaign.sent_count}</span>
                    {' / '}
                    <span className="text-red-600">{campaign.failed_count}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {campaign.sent_at ? formatDate(campaign.sent_at) : formatDate(campaign.created_at)}
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    {campaign.status === 'draft' && (
                      <>
                        <button
                          onClick={() => handleSendCampaign(campaign.id)}
                          disabled={sending}
                          className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                        >
                          Send
                        </button>
                        <button
                          onClick={() => handleDeleteCampaign(campaign.id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </>
                    )}
                    {campaign.status === 'scheduled' && (
                      <button
                        onClick={() => handleSendCampaign(campaign.id)}
                        disabled={sending}
                        className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                      >
                        Send Now
                      </button>
                    )}
                    {campaign.status === 'sent' && (
                      <button
                        onClick={() => setSelectedCampaign(campaign)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        View
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Campaign */}
      {activeTab === 'create' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 space-y-6">
          {/* Template Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              📝 Start with a Template
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {templates.map(template => (
                <div
                  key={template.id}
                  className={`p-3 text-left border rounded-lg transition-all ${
                    formData.template_type === template.id
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
                  }`}
                >
                  <button
                    onClick={() => handleTemplateSelect(template.id)}
                    className="w-full text-left"
                    data-testid={`template-use-${template.id}`}
                  >
                    <div className="font-medium text-sm text-gray-900 dark:text-white flex items-center gap-1">
                      {template.name}
                      {template.is_customized && (
                        <span className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 px-1.5 py-0.5 rounded">Edited</span>
                      )}
                    </div>
                  </button>
                  <div className="mt-1.5 flex gap-1">
                    <button
                      onClick={() => handleEditTemplate(template)}
                      className="text-[11px] text-gray-500 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400"
                      data-testid={`template-edit-${template.id}`}
                    >
                      Edit
                    </button>
                    {template.is_customized && (
                      <button
                        onClick={() => handleResetTemplate(template.id)}
                        className="text-[11px] text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        data-testid={`template-reset-${template.id}`}
                      >
                        Reset
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Template Editor */}
            {editingTemplate && (
              <div className="mt-4 border border-purple-300 dark:border-purple-700 rounded-lg p-4 bg-purple-50/50 dark:bg-purple-900/10" data-testid="template-editor">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                    Editing: {editingTemplate.name}
                  </h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setTemplatePreview(!templatePreview)}
                      className="text-xs px-3 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      {templatePreview ? 'Edit HTML' : 'Preview'}
                    </button>
                    <button
                      onClick={() => setEditingTemplate(null)}
                      className="text-xs px-3 py-1 rounded text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Template Name</label>
                    <input
                      type="text"
                      value={templateForm.name}
                      onChange={e => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Subject Line</label>
                    <input
                      type="text"
                      value={templateForm.subject}
                      onChange={e => setTemplateForm(prev => ({ ...prev, subject: e.target.value }))}
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">HTML Content</label>
                    {templatePreview ? (
                      <div 
                        className="w-full min-h-[300px] max-h-[500px] overflow-auto border border-gray-300 dark:border-gray-600 rounded bg-white p-4"
                        dangerouslySetInnerHTML={{ __html: templateForm.body_html }}
                      />
                    ) : (
                      <textarea
                        value={templateForm.body_html}
                        onChange={e => setTemplateForm(prev => ({ ...prev, body_html: e.target.value }))}
                        rows={12}
                        className="w-full px-3 py-2 text-xs font-mono border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-green-400"
                        spellCheck={false}
                      />
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveTemplate}
                      disabled={savingTemplate}
                      className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50"
                      data-testid="template-save-btn"
                    >
                      {savingTemplate ? 'Saving...' : 'Save Template'}
                    </button>
                    <button
                      onClick={() => handleCopyHtml(templateForm.body_html)}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      {copySuccess ? 'Copied!' : 'Copy HTML'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Campaign Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Campaign Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., App Launch Announcement"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Recipients */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              📬 Recipients
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {RECIPIENT_TYPES.map(type => (
                <button
                  key={type.value}
                  onClick={() => setFormData(prev => ({ ...prev, recipient_type: type.value }))}
                  className={`p-4 text-left border rounded-lg transition-all ${
                    formData.recipient_type === type.value
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
                  }`}
                >
                  <div className="font-medium text-gray-900 dark:text-white">{type.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{type.description}</div>
                </button>
              ))}
            </div>
            
            {formData.recipient_type === 'custom' && (
              <textarea
                value={formData.custom_emails}
                onChange={e => setFormData(prev => ({ ...prev, custom_emails: e.target.value }))}
                placeholder="Enter email addresses (one per line or comma-separated)"
                rows={4}
                className="mt-3 w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            )}
            
            <div className="mt-2 text-sm text-purple-600 dark:text-purple-400 font-medium">
              📊 {recipientCount} recipient{recipientCount !== 1 ? 's' : ''} will receive this email
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Subject Line *
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={e => setFormData(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="e.g., 🚀 Exciting News: We've Launched!"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="mt-1 text-xs text-gray-500">Use {"{{name}}"} and {"{{email}}"} for personalization</p>
          </div>

          {/* Body */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Email Body (HTML) *
              </label>
              <button
                onClick={() => setPreviewMode(!previewMode)}
                className="text-sm text-purple-600 hover:text-purple-800"
              >
                {previewMode ? '✏️ Edit' : '👁️ Preview'}
              </button>
            </div>
            
            {previewMode ? (
              <div 
                className="w-full min-h-[400px] p-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-white overflow-auto"
                dangerouslySetInnerHTML={{ __html: formData.body_html.replace(/\{\{name\}\}/g, 'John').replace(/\{\{email\}\}/g, 'john@example.com') }}
              />
            ) : (
              <textarea
                value={formData.body_html}
                onChange={e => setFormData(prev => ({ ...prev, body_html: e.target.value }))}
                placeholder="Paste your HTML email content here..."
                rows={12}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
              />
            )}
          </div>

          {/* Schedule */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              📅 Schedule (Optional)
            </label>
            <input
              type="datetime-local"
              value={formData.scheduled_at}
              onChange={e => setFormData(prev => ({ ...prev, scheduled_at: e.target.value }))}
              className="w-full md:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="mt-1 text-xs text-gray-500">Leave empty to save as draft or send immediately</p>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-4 border-t dark:border-gray-700">
            <button
              onClick={() => handleCreateCampaign(false)}
              disabled={sending}
              className="px-6 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-all disabled:opacity-50"
            >
              {formData.scheduled_at ? '📅 Schedule' : '💾 Save Draft'}
            </button>
            <button
              onClick={() => handleCreateCampaign(true)}
              disabled={sending || recipientCount === 0}
              className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {sending ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  Sending...
                </>
              ) : (
                <>🚀 Send Now ({recipientCount})</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Logs */}
      {activeTab === 'logs' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
          <div className="p-4 border-b dark:border-gray-700">
            <h3 className="font-medium text-gray-900 dark:text-white">📊 Campaign Send Logs</h3>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {logs.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                No logs yet. Send your first campaign to see logs here.
              </div>
            ) : logs.map((log, idx) => (
              <div key={idx} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">{log.campaign_name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{log.subject}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm">
                      <span className="text-green-600 font-medium">{log.total_sent} sent</span>
                      {log.total_failed > 0 && (
                        <span className="text-red-600 ml-2">{log.total_failed} failed</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400">{formatDate(log.sent_at)}</div>
                  </div>
                </div>
                <details className="mt-2">
                  <summary className="text-sm text-purple-600 cursor-pointer hover:text-purple-800">
                    View {log.recipients?.length || 0} recipients
                  </summary>
                  <div className="mt-2 max-h-40 overflow-auto bg-gray-50 dark:bg-gray-900 rounded p-2">
                    {log.recipients?.map((r, i) => (
                      <div key={i} className="text-xs py-1 flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{r.email}</span>
                        <span className={r.status === 'sent' ? 'text-green-600' : 'text-red-600'}>
                          {r.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Campaign Detail Modal */}
      {selectedCampaign && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">{selectedCampaign.name}</h3>
                  <p className="text-gray-500">{selectedCampaign.subject}</p>
                </div>
                <button
                  onClick={() => { setSelectedCampaign(null); setViewHtmlMode(false); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{selectedCampaign.total_recipients}</div>
                  <div className="text-xs text-gray-500">Total</div>
                </div>
                <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{selectedCampaign.sent_count}</div>
                  <div className="text-xs text-gray-500">Sent</div>
                </div>
                <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{selectedCampaign.failed_count}</div>
                  <div className="text-xs text-gray-500">Failed</div>
                </div>
              </div>
              
              {/* Toggle: Rendered vs HTML Source */}
              <div className="flex items-center gap-2 mb-3">
                <button
                  onClick={() => setViewHtmlMode(false)}
                  className={`text-sm px-3 py-1 rounded ${!viewHtmlMode ? 'bg-purple-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                  data-testid="view-rendered-btn"
                >
                  Preview
                </button>
                <button
                  onClick={() => setViewHtmlMode(true)}
                  className={`text-sm px-3 py-1 rounded ${viewHtmlMode ? 'bg-purple-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                  data-testid="view-html-btn"
                >
                  View HTML
                </button>
                <button
                  onClick={() => handleCopyHtml(selectedCampaign.body_html)}
                  className="ml-auto text-sm px-3 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  data-testid="copy-html-btn"
                >
                  {copySuccess ? 'Copied!' : 'Copy HTML'}
                </button>
              </div>
              
              {viewHtmlMode ? (
                <pre className="border dark:border-gray-700 rounded-lg p-4 bg-gray-900 text-green-400 text-xs font-mono max-h-96 overflow-auto whitespace-pre-wrap break-words">
                  {selectedCampaign.body_html}
                </pre>
              ) : (
                <div 
                  className="border dark:border-gray-700 rounded-lg p-4 bg-white max-h-96 overflow-auto"
                  dangerouslySetInnerHTML={{ __html: selectedCampaign.body_html }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailCampaignManager;
